import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/common';
import { ServiceForm } from '../ServiceForm';
import { useUIStore } from '@/store/uiStore';
import { toast } from '@/store/toastStore';
import { serviceApi } from '@/api/endpoints/services';
import { Service, CreateServiceRequest, UpdateServiceRequest, ServiceLine } from '@/types/service';
import {
  detectServiceLineChanges,
  validateServiceLines,
  filterValidServiceLines,
  prepareServiceLineForApi
} from '@/utils/serviceLineChanges';

// Extended type for service lines in the form with additional tracking properties
type FormServiceLine = ServiceLine & { _isOriginal?: boolean };

interface EditServiceModalProps {
  onSuccess?: () => void;
}

export const EditServiceModal: React.FC<EditServiceModalProps> = ({
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { modals, closeModal, modalData, refreshCallbacks } = useUIStore();
  const [loading, setLoading] = useState(false);

  const isOpen = modals.editService || false;
  const service = modalData?.service as Service | undefined;

  const handleServiceLineChanges = async (service: Service, currentServiceLines: ServiceLine[]) => {
    // Filter out invalid service lines (those without materials)
    const validServiceLines = filterValidServiceLines(currentServiceLines);

    // Validate service lines
    const validationErrors = validateServiceLines(validServiceLines);
    if (validationErrors.length > 0) {
      throw new Error(`Service line validation failed:\n${validationErrors.slice(0, 3).join('\n')}${validationErrors.length > 3 ? `\n... and ${validationErrors.length - 3} more validation errors` : ''}`);
    }

    // Helper function to clean service line by removing _isOriginal property
    const cleanServiceLine = (line: FormServiceLine): ServiceLine => {
      const { _isOriginal, ...cleanLine } = line;
      return cleanLine;
    };

    // Separate original service lines from new ones
    const originalServiceLines = validServiceLines.filter(line => (line as FormServiceLine)._isOriginal);
    const newServiceLines = validServiceLines.filter(line => !(line as FormServiceLine)._isOriginal);

    // Only process changes for original service lines (user-modified ones)
    const originalLinesForComparison = originalServiceLines.map(cleanServiceLine);

    // Detect changes between original and current service lines (only for original lines)
    const changes = detectServiceLineChanges(service.serviceLines || [], originalLinesForComparison);

    // Add new service lines to the changes
    changes.toCreate.push(...newServiceLines.map(cleanServiceLine));

    const results = {
      created: 0,
      updated: 0,
      deleted: 0,
      errors: [] as string[]
    };

    // Process deletions first
    for (const serviceLineToDelete of changes.toDelete) {
      try {
        await serviceApi.deleteServiceLine(serviceLineToDelete.code);
        results.deleted++;
      } catch (error: any) {
        const errorMsg = `Failed to delete service line ${serviceLineToDelete.materialName || serviceLineToDelete.materialId}: ${error.message}`;
        results.errors.push(errorMsg);
        console.error(errorMsg, error);
      }
    }

    // Process updates
    for (const serviceLineToUpdate of changes.toUpdate) {
      try {
        const updateData = prepareServiceLineForApi(serviceLineToUpdate);
        await serviceApi.updateServiceLine(serviceLineToUpdate.code, {
          serviceId: service.code, // Use service code as backend expects
          ...updateData
        });
        results.updated++;
      } catch (error: any) {
        const errorMsg = `Failed to update service line ${serviceLineToUpdate.materialName || serviceLineToUpdate.materialId}: ${error.message}`;
        results.errors.push(errorMsg);
        console.error(errorMsg, error);
      }
    }

    // Process creations
    for (const serviceLineToCreate of changes.toCreate) {
      try {
        const createData = prepareServiceLineForApi(serviceLineToCreate);
        await serviceApi.createServiceLine(service.code, createData); // Use service code as backend expects
        results.created++;
      } catch (error: any) {
        const errorMsg = `Failed to create service line ${serviceLineToCreate.materialName || serviceLineToCreate.materialId}: ${error.message}`;
        results.errors.push(errorMsg);
        console.error(errorMsg, error);
      }
    }

    // Log results for debugging
    console.log('Service line changes processed:', results);

    // Handle results and provide user feedback
    const totalOperations = results.created + results.updated + results.deleted;
    const hasErrors = results.errors.length > 0;
    const hasSuccesses = totalOperations > 0;

    if (hasErrors && !hasSuccesses) {
      // All operations failed
      const errorMessage = `Service line operations failed:\n${results.errors.slice(0, 3).join('\n')}${results.errors.length > 3 ? `\n... and ${results.errors.length - 3} more errors` : ''}`;
      throw new Error(errorMessage);
    } else if (hasErrors && hasSuccesses) {
      // Partial success - some operations failed
      const successParts = [];
      if (results.created > 0) successParts.push(`${results.created} created`);
      if (results.updated > 0) successParts.push(`${results.updated} updated`);
      if (results.deleted > 0) successParts.push(`${results.deleted} deleted`);

      const successMessage = `Service lines partially updated: ${successParts.join(', ')}`;
      const errorSummary = `${results.errors.length} operation(s) failed. Check console for details.`;

      toast.warning('Partial Success', `${successMessage}\n\n${errorSummary}`);
      console.warn('Service line operation errors:', results.errors);
    } else if (hasSuccesses) {
      // All operations succeeded
      const successParts = [];
      if (results.created > 0) successParts.push(`${results.created} created`);
      if (results.updated > 0) successParts.push(`${results.updated} updated`);
      if (results.deleted > 0) successParts.push(`${results.deleted} deleted`);

      console.log(`Service lines successfully updated: ${successParts.join(', ')}`);
    }
    // If no operations were needed, that's fine - no feedback needed
  };

  const handleSubmit = async (data: CreateServiceRequest | UpdateServiceRequest, serviceLines?: ServiceLine[]) => {
    if (!service) return;

    setLoading(true);
    let serviceUpdated = false;

    try {
      const updateData: UpdateServiceRequest = {
        id: service.id,
        ...(data as CreateServiceRequest)
      };

      // Step 1: Update the service
      try {
        await serviceApi.updateService({ ...updateData, code: service.code });
        serviceUpdated = true;
      } catch (error: any) {
        console.error('Error updating service:', error);
        throw new Error(`Failed to update service: ${error.message || 'Unknown error'}`);
      }

      // Step 2: Handle service line changes
      if (serviceLines) {
        try {
          await handleServiceLineChanges(service, serviceLines);
          // serviceLinesProcessed = true;
        } catch (error: any) {
          console.error('Error processing service lines:', error);
          // If service was updated but service lines failed, show partial success
          if (serviceUpdated) {
            toast.warning('Partial Success',
              `Service updated successfully, but service line operations failed:\n${error.message}`
            );
            closeModal('editService');
            onSuccess?.();
            refreshCallbacks.services?.();
            return;
          } else {
            throw error; // Re-throw if service update also failed
          }
        }
      } else {
        // serviceLinesProcessed = true; // No service lines to process
      }

      // Success - both service and service lines processed successfully
      toast.success('Success', t('services.messages.updateSuccess'));
      closeModal('editService');
      onSuccess?.();
      refreshCallbacks.services?.();

    } catch (error: any) {
      console.error('Error in service update process:', error);

      // Provide specific error messages based on what failed
      let errorMessage = error.message || t('services.messages.updateError');

      if (!serviceUpdated) {
        errorMessage = `Failed to update service: ${errorMessage}`;
      }

      toast.error('Error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndClose = async (data: CreateServiceRequest | UpdateServiceRequest, serviceLines?: ServiceLine[]) => {
    await handleSubmit(data, serviceLines);
  };

  const handleCancel = () => {
    closeModal('editService');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title={t('services.modals.editTitle')}
      size="xl"
      zIndex={60}
    >
      <ServiceForm
        service={service}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        onSaveAndClose={handleSaveAndClose}
        loading={loading}
      />
    </Modal>
  );
};
